import { apiGet, apiSend } from '../../session/client';
import { type OfferCard, type OpeningHours } from '../store/store-api';

/**
 * Everything "Places near you" reads and writes. Every call is a platform
 * route: the storefront directory (`GET consumer/storefronts`, stage 7), the
 * taxonomy, the signed-in person's saved addresses, and "notify me" for a
 * postcode nobody serves yet. Nothing here invents a value.
 */

export type Mode = 'delivery' | 'collection' | 'inperson';
export type Sort = 'recommended' | 'nearest' | 'rating';

export interface DirectoryPlace {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly kindLabel: string;
  readonly shortDescription?: string;
  readonly distanceMiles?: number;
  readonly coordinates?: { readonly latitude: number; readonly longitude: number };
  readonly town?: string;
  readonly postcode?: string;
  readonly fulfilmentModes: readonly string[];
  readonly openingHours?: readonly OpeningHours[];
  readonly openNow?: boolean;
  readonly rating?: number;
  readonly reviewCount?: number;
  readonly deliveryFromMinor?: number;
  readonly minimumOrderMinor?: number;
  readonly currency: string;
  readonly deliveryEtaLabel?: string;
  readonly collectionEtaLabel?: string;
  readonly registration?: { readonly regulator: string; readonly number: string };
  readonly nhs: boolean;
  readonly promotions: readonly string[];
  readonly products: number;
  readonly services: number;
  readonly bookableServices: number;
  readonly rows: readonly OfferCard[];
}

export interface DirectoryPage {
  readonly near: { readonly postcode?: string; readonly displayArea?: string };
  readonly reachable: number;
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly hasMore: boolean;
  readonly complete: boolean;
  readonly places: readonly DirectoryPlace[];
  readonly needs: readonly { readonly categoryId: string; readonly places: number }[];
  readonly regulators: readonly { readonly regulator: string; readonly places: number }[];
  readonly nhsRecorded: boolean;
}

export interface DirectoryRequest {
  readonly postcode: string;
  readonly mode?: Mode;
  readonly need?: readonly string[];
  readonly category?: readonly string[];
  readonly kind?: 'PRODUCT' | 'SERVICE';
  readonly q?: string;
  readonly open?: boolean;
  readonly rating?: number;
  readonly offers?: boolean;
  readonly freeDelivery?: boolean;
  readonly nhs?: boolean;
  readonly regulator?: string;
  readonly maxMiles?: number;
  readonly sort?: Sort;
  readonly page?: number;
  readonly limit?: number;
}

export interface TaxonomyCategory {
  readonly id: string;
  readonly name: string;
  readonly sector?: string;
  readonly parentId?: string;
  readonly depth?: number;
  readonly itemKinds?: readonly string[];
  readonly imageUrl?: string;
}

/** The query string, in a stable order so it can key a load. */
export function directoryQuery(request: DirectoryRequest): string {
  const params = new URLSearchParams();
  params.set('postcode', request.postcode);
  if (request.mode !== undefined) params.set('mode', request.mode);
  if (request.need !== undefined && request.need.length > 0) params.set('need', request.need.join(','));
  if (request.category !== undefined && request.category.length > 0) {
    params.set('category', request.category.join(','));
  }
  if (request.kind !== undefined) params.set('kind', request.kind);
  if (request.q !== undefined && request.q.trim().length >= 2) params.set('q', request.q.trim());
  if (request.open === true) params.set('open', 'true');
  if (request.rating !== undefined) params.set('rating', String(request.rating));
  if (request.offers === true) params.set('offers', 'true');
  if (request.freeDelivery === true) params.set('freeDelivery', 'true');
  if (request.nhs === true) params.set('nhs', 'true');
  if (request.regulator !== undefined) params.set('regulator', request.regulator);
  if (request.maxMiles !== undefined) params.set('maxMiles', String(request.maxMiles));
  if (request.sort !== undefined && request.sort !== 'recommended') params.set('sort', request.sort);
  if (request.page !== undefined && request.page > 1) params.set('page', String(request.page));
  if (request.limit !== undefined) params.set('limit', String(request.limit));
  return `?${params.toString()}`;
}

export const getDirectory = (request: DirectoryRequest): Promise<DirectoryPage> =>
  apiGet<DirectoryPage>(`/consumer/storefronts${directoryQuery(request)}`);

export const getCategories = (): Promise<readonly TaxonomyCategory[]> =>
  apiGet<{ categories: TaxonomyCategory[] }>('/consumer/categories').then((body) => body.categories);

/** The signed-in person's saved addresses — the default one sets the page's postcode. */
export const getSavedAddresses = (): Promise<
  readonly { readonly postcode: string; readonly isDefault: boolean }[]
> =>
  apiGet<{ addresses: { postcode: string; isDefault: boolean }[] }>('/person/me/addresses').then(
    (b) => b.addresses,
  );

/** "Notify me when you're here" — the platform's own launch-interest record. */
export const registerInterest = (postcode: string, email?: string): Promise<{ readonly created: boolean }> =>
  apiSend<{ created: boolean }>('POST', '/consumer/location/interest', {
    postcode,
    ...(email !== undefined && email.trim().length > 0 ? { email: email.trim() } : {}),
  });
